
import { format, parse } from 'date-fns';
import { es } from 'date-fns/locale';
import { NutritionJournal } from '@/types';

export const getMenuDayFromDate = (date: Date): string => {
  // Get day name in lowercase Spanish
  const dayName = format(date, 'EEEE', { locale: es }).toLowerCase();
  
  // Map Spanish day names to English day names as used in the menu
  const dayMap: Record<string, string> = {
    'lunes': 'monday',
    'martes': 'tuesday',
    'miércoles': 'wednesday',
    'jueves': 'thursday',
    'viernes': 'friday',
    'sábado': 'saturday',
    'domingo': 'sunday'
  };
  
  return dayMap[dayName] || 'monday'; // Default to monday if not found
};

// Keep track of sync attempts to prevent infinite loops
const syncAttempts = new Map<string, number>();
const MAX_SYNC_ATTEMPTS = 3;
const SYNC_ATTEMPT_TIMEOUT = 60000; // 1 minute reset timeout

// Keep track of API call timestamps to prevent rate limiting
const apiCallTimestamps = new Map<string, number[]>();
const MAX_CALLS_PER_MINUTE = 10; // Reduced from 20 to 10
const RATE_LIMIT_WINDOW = 60000; // 1 minute

// Track last menu sync for each journal to prevent loops
const lastMenuSync = new Map<string, {
  timestamp: number,
  hash: string
}>();

// Track in-progress syncs to prevent concurrent operations
const inProgressSync = new Map<string, boolean>();

// Track sync operations to prevent rapid changes
const lastSyncOperation = new Map<string, number>();
const MIN_SYNC_INTERVAL = 5000; // 5 seconds between syncs for the same item

// Check if we would exceed rate limits
const checkRateLimit = (endpoint: string): boolean => {
  const now = Date.now();
  const oneMinuteAgo = now - RATE_LIMIT_WINDOW;
  
  // Get or initialize the timestamp array for this endpoint
  let timestamps = apiCallTimestamps.get(endpoint) || [];
  
  // Remove timestamps older than 1 minute
  timestamps = timestamps.filter(time => time > oneMinuteAgo);
  
  // Update the timestamps in the map
  apiCallTimestamps.set(endpoint, timestamps);
  
  // Check if we exceed the rate limit
  if (timestamps.length >= MAX_CALLS_PER_MINUTE) {
    console.warn(`Rate limit exceeded for ${endpoint}, throttling requests`);
    return true; // Rate limit exceeded
  }
  
  // Record this call
  timestamps.push(now);
  apiCallTimestamps.set(endpoint, timestamps);
  
  return false; // Rate limit not exceeded
}

// Helper function to calculate a simple hash of an object
const calculateHash = (obj: any): string => {
  if (!obj) return '';
  try {
    return JSON.stringify(obj);
  } catch (e) {
    return String(Date.now());
  }
};

// Check if sync operation should proceed
const shouldAllowSync = (syncKey: string): boolean => {
  const now = Date.now();
  const lastSync = lastSyncOperation.get(syncKey) || 0;
  
  if (now - lastSync < MIN_SYNC_INTERVAL) {
    console.log(`[SYNC] Operation ${syncKey} throttled. Last sync: ${now - lastSync}ms ago, minimum interval: ${MIN_SYNC_INTERVAL}ms`);
    return false;
  }
  
  lastSyncOperation.set(syncKey, now);
  return true;
};

export const syncJournalWithMenu = async (
  journal: NutritionJournal, 
  menuDay: string, 
  menuTotals: any,
  userId: string
): Promise<NutritionJournal> => {
  // Create a unique sync key to track this operation
  const syncKey = `journal_menu_sync_${journal.id}_${menuDay}`;
  
  // Only proceed if not synced recently
  if (!shouldAllowSync(syncKey)) {
    return journal;
  }
  
  // Check if another sync is already in progress
  if (inProgressSync.get(syncKey)) {
    console.log(`[JOURNAL] Sync already in progress for ${journal.id}, skipping`);
    return journal;
  }
  
  // Mark sync as in progress
  inProgressSync.set(syncKey, true);
  
  try {
    // Create a deep copy of the journal to avoid direct mutations
    const updatedJournal = JSON.parse(JSON.stringify(journal));
    
    // Don't update if there are no totals or they're already set
    if (!menuTotals || !updatedJournal.nutritionSummary) {
      inProgressSync.set(syncKey, false);
      return updatedJournal;
    }
    
    // Check if we've recently synced this journal with the same menu data
    const journalId = updatedJournal.id;
    const menuHash = calculateHash(menuTotals);
    const lastSync = lastMenuSync.get(journalId);
    const now = Date.now();
    
    // Only proceed if:
    // 1. No previous sync exists, OR
    // 2. More than 30 seconds have passed, OR
    // 3. The menu data has changed
    if (lastSync) {
      const timeSinceLastSync = now - lastSync.timestamp;
      const sameMenuData = lastSync.hash === menuHash;
      
      if (timeSinceLastSync < 60000 && sameMenuData) { // Increased to 60 seconds
        console.log(`[JOURNAL] Skipping menu sync - last sync was ${Math.round(timeSinceLastSync/1000)}s ago with same data`);
        inProgressSync.set(syncKey, false);
        return updatedJournal;
      }
    }
    
    // Only update if consumption is zero (to avoid overwriting manual entries)
    const ns = updatedJournal.nutritionSummary;
    let updated = false;
    
    if (ns.caloriesConsumed === 0 && menuTotals.calories) {
      ns.caloriesConsumed = Math.round(menuTotals.calories);
      updated = true;
    }
    
    if (ns.proteinConsumed === 0 && menuTotals.protein) {
      ns.proteinConsumed = Math.round(menuTotals.protein);
      updated = true;
    }
    
    if (ns.carbsConsumed === 0 && menuTotals.carbs) {
      ns.carbsConsumed = Math.round(menuTotals.carbs);
      updated = true;
    }
    
    if (ns.fatConsumed === 0 && menuTotals.fat) {
      ns.fatConsumed = Math.round(menuTotals.fat);
      updated = true;
    }
    
    // Handle micronutrients if they exist in the menu totals
    if (ns.ironConsumed === 0 && menuTotals.iron) {
      ns.ironConsumed = Number(menuTotals.iron.toFixed(1));
      updated = true;
    }
    
    if (ns.calciumConsumed === 0 && menuTotals.calcium) {
      ns.calciumConsumed = Math.round(menuTotals.calcium);
      updated = true;
    }
    
    if (ns.zincConsumed === 0 && menuTotals.zinc) {
      ns.zincConsumed = Number(menuTotals.zinc.toFixed(1));
      updated = true;
    }
    
    if (ns.magnesiumConsumed === 0 && menuTotals.magnesium) {
      ns.magnesiumConsumed = Math.round(menuTotals.magnesium);
      updated = true;
    }
    
    if (ns.omega3Consumed === 0 && menuTotals.omega3) {
      ns.omega3Consumed = Number(menuTotals.omega3.toFixed(1));
      updated = true;
    }
    
    // Record this sync to prevent future unnecessary updates
    lastMenuSync.set(journalId, {
      timestamp: now,
      hash: menuHash
    });
    
    // Only log if we actually updated something
    if (updated) {
      console.log('[JOURNAL] After menu sync:', ns);

      // Use a controlled approach for events
      setTimeout(() => {
        try {
          if (inProgressSync.get(syncKey) && shouldAllowSync(`event_${syncKey}`)) {
            const weightChangeEvent = new CustomEvent('weight_nutrition_updated', {
              detail: { 
                weight: updatedJournal.weight,
                nutritionSummary: ns,
                timestamp: Date.now()
              }
            });
            window.dispatchEvent(weightChangeEvent);
          }
        } catch (error) {
          console.error('Error dispatching weight change event:', error);
        }
      }, 1000);
    } else {
      console.log('[JOURNAL] No updates needed from menu sync');
    }
    
    return updatedJournal;
  } catch (error) {
    console.error('Error in syncJournalWithMenu:', error);
    return journal;
  } finally {
    // Set a timeout to clear the in-progress flag after a safety period
    setTimeout(() => {
      inProgressSync.set(syncKey, false);
    }, 5000); // 5 seconds safety timeout
  }
};

export const syncMenuWithProfile = async (
  weeklyMenu: any, 
  userId: string, 
  nutritionGoals: any
): Promise<any> => {
  if (!weeklyMenu || !userId || !nutritionGoals) {
    return weeklyMenu;
  }
  
  // Create a unique key for this sync operation
  const syncKey = `menu_sync_${userId}_${weeklyMenu?.week || 'current'}`;
  
  // Only proceed if not synced recently
  if (!shouldAllowSync(syncKey)) {
    return weeklyMenu;
  }
  
  // Check if another sync is already in progress
  if (inProgressSync.get(syncKey)) {
    console.log(`[MENU] Sync already in progress for ${syncKey}, skipping`);
    return weeklyMenu;
  }
  
  // Mark sync as in progress
  inProgressSync.set(syncKey, true);
  
  try {
    // Check if we're exceeding rate limits
    if (checkRateLimit('syncMenuWithProfile')) {
      console.warn(`Rate limit exceeded for syncMenuWithProfile. Skipping operation.`);
      return weeklyMenu;
    }
    
    // Check if we've had too many attempts recently
    const attempts = syncAttempts.get(syncKey) || 0;
    if (attempts >= MAX_SYNC_ATTEMPTS) {
      console.warn(`Too many sync attempts for menu: ${syncKey}. Skipping sync to prevent loop.`);
      
      // Reset the counter after the timeout
      setTimeout(() => {
        syncAttempts.delete(syncKey);
      }, SYNC_ATTEMPT_TIMEOUT);
      
      return weeklyMenu;
    }
    
    console.log('[MENU] Synchronizing menu with profile goals:', nutritionGoals);
    
    // Track this attempt
    syncAttempts.set(syncKey, attempts + 1);
    
    // Create a deep copy of the menu to avoid direct mutations
    const updatedMenu = JSON.parse(JSON.stringify(weeklyMenu));
    
    // Update the nutrition goals with the profile data
    updatedMenu.nutritionGoals = {
      calories: parseFloat(String(nutritionGoals.calories)) || 2000,
      protein: parseFloat(String(nutritionGoals.protein)) || 75,
      carbs: parseFloat(String(nutritionGoals.carbs)) || 250,
      fat: parseFloat(String(nutritionGoals.fat)) || 65,
      iron: parseFloat(String(nutritionGoals.iron)) || 18,
      calcium: parseFloat(String(nutritionGoals.calcium)) || 1000,
      zinc: parseFloat(String(nutritionGoals.zinc)) || 11,
      magnesium: parseFloat(String(nutritionGoals.magnesium)) || 400,
      omega3: parseFloat(String(nutritionGoals.omega3)) || 1.6
    };
    
    // Validate all nutrition goal values
    Object.entries(updatedMenu.nutritionGoals).forEach(([key, value]) => {
      const numValue = value as number;
      if (isNaN(numValue) || numValue < 0) {
        updatedMenu.nutritionGoals[key] = key === 'protein' ? 75 : 
                                        key === 'calories' ? 2000 : 
                                        key === 'carbs' ? 250 : 
                                        key === 'fat' ? 65 : 
                                        key === 'iron' ? 18 :
                                        key === 'calcium' ? 1000 :
                                        key === 'zinc' ? 11 :
                                        key === 'magnesium' ? 400 : 1.6;
      }
    });
    
    console.log('[MENU] After profile sync:', updatedMenu.nutritionGoals);
    
    // More controlled event dispatching with retry prevention
    setTimeout(() => {
      try {
        if (inProgressSync.get(syncKey) && shouldAllowSync(`event_${syncKey}`)) {
          const goalsUpdateEvent = new CustomEvent('nutrition_goals_updated', {
            detail: { 
              nutritionGoals: updatedMenu.nutritionGoals,
              timestamp: Date.now()
            }
          });
          window.dispatchEvent(goalsUpdateEvent);
        }
      } catch (error) {
        console.error('Error dispatching nutrition goals update event:', error);
      }
    }, 1000);
    
    // Reset the counter after successful sync
    setTimeout(() => {
      syncAttempts.delete(syncKey);
      inProgressSync.set(syncKey, false);
    }, SYNC_ATTEMPT_TIMEOUT);
    
    return updatedMenu;
  } catch (error) {
    console.error('Error syncing menu with profile:', error);
    
    // Still reset the counter after some time
    setTimeout(() => {
      syncAttempts.delete(syncKey);
      inProgressSync.set(syncKey, false);
    }, SYNC_ATTEMPT_TIMEOUT);
    
    return weeklyMenu;
  }
};
